// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import {
    Grid,
    GridCol,
    Item,
    Translate,
} from '@plesk/ui-library';
import { IFailoverDomainDetailResponse } from 'common/api/resources/FailoverDomain';
import { IComputeResourceResponse } from 'common/api/resources/ComputeResource/model';
import {
    IIpBlockResponse,
    IpBlockType,
    IpListType,
} from 'common/api/resources/IpBlock/model';
import { pathTo } from 'common/helpers/core';
import { formatSizeFromGiB } from 'common/helpers/units';
import { SIZE } from 'common/constants';
import CardHeader from 'admin/dashboard/components/CardHeader';
import { SankeyDiagram } from 'admin/failoverDomain/page/components/SummaryCards/SankeyDiagram';
import { buildFdSankeyData } from 'admin/failoverDomain/page/components/SummaryCards/buildFdSankeyData';
import {
    ActivityCount,
    CardItemContainer,
    EmptyCardText,
    IpBlockLink,
    IpBlockRow,
    IpBlockUsage,
    LegendLabel,
    LegendRow,
    LegendValue,
    StackedCardsContainer,
    StorageContent,
    StorageName,
} from 'admin/failoverDomain/page/components/SummaryCards/Styles';

// ── Formatting helpers ────────────────────────────────────────────────────────

const formatIpCount = (n: number): string => {
    if (n >= 1e12) { return `${(n / 1e12).toFixed(0)} T`; }
    if (n >= 1e9) { return `${(n / 1e9).toFixed(0)} B`; }
    if (n >= 1e6) { return `${(n / 1e6).toFixed(0)} M`; }
    if (n >= 1000) { return `${Math.round(n / 1000)} K`; }
    return String(n);
};

const ipv4ToInt = (ip: string): number => {
    const parts = ip.split('.').map(Number);
    if (parts.length !== 4 || parts.some(p => !Number.isFinite(p))) {
        return 0;
    }
    // eslint-disable-next-line no-bitwise
    return ((parts[0] * 256 + parts[1]) * 256 + parts[2]) * 256 + parts[3];
};

// `IpBlockResource.total_ips_count` is a display string: numeric for
// `list_type=set`; a range ("10.0.0.1 - 10.0.0.10") or CIDR ("2001:db8::/64")
// for `list_type=range`. Compute an actual count so the row reads "X / N" with
// N as a number. IPv6 ranges beyond ~2^40 IPs are unhelpful as a count and
// fall back to the backend-provided CIDR string.
const IPV6_MAX_COUNTABLE_HOST_BITS = 40;

const formatIpBlockTotal = (block: IIpBlockResponse): string => {
    if (block.list_type === IpListType.SET) {
        const n = Number(block.total_ips_count);
        return Number.isFinite(n) ? formatIpCount(n) : block.total_ips_count;
    }
    if (block.type === IpBlockType.IPv4) {
        return formatIpCount(ipv4ToInt(block.to) - ipv4ToInt(block.from) + 1);
    }
    const prefix = Number(block.subnet);
    if (!Number.isFinite(prefix) || prefix < 0 || prefix > 128) {
        return block.total_ips_count;
    }
    const hostBits = 128 - prefix;
    return hostBits > IPV6_MAX_COUNTABLE_HOST_BITS
        ? block.total_ips_count
        : formatIpCount(Math.pow(2, hostBits));
};

// ── Component ─────────────────────────────────────────────────────────────────

export interface ISummaryCardsProps {
    failoverDomain: IFailoverDomainDetailResponse;
    computeResources: IComputeResourceResponse[];
}

const SummaryCards: React.FC<ISummaryCardsProps> = ({ failoverDomain, computeResources }) => {
    const sankeyData = React.useMemo(
        () => buildFdSankeyData(computeResources),
        [computeResources]
    );

    const { storage } = failoverDomain;

    return (
        <Grid gap={SIZE.XS} style={{ alignItems: 'stretch' }}>

            {/* Sankey: CR count + RAM flow */}
            <GridCol lg={7}>
                <CardItemContainer>
                    <Item view="card">
                        <SankeyDiagram
                            nodes={sankeyData.nodes}
                            links={sankeyData.links}
                            leftTitle={`Compute Resources: ${failoverDomain.total_compute_resources_count}`}
                            rightTitle={`RAM: ${sankeyData.totalRamGiB} GiB`}
                            formatValue={sankeyData.formatValue}
                        />
                    </Item>
                </CardItemContainer>
            </GridCol>

            {/* Storage free space */}
            <GridCol lg={2}>
                <CardItemContainer>
                    <Item
                        view="card"
                        title={
                            // todo: SVM2-7206 redirect to storage overview page
                            <CardHeader to={pathTo('storage')}>
                                <Translate content="failoverDomain.page.cards.storage" />
                            </CardHeader>
                        }
                    >
                        <StorageName href={pathTo('storage')}>{storage.name}</StorageName>
                        <StorageContent>
                            <LegendRow>
                                <LegendLabel>
                                    <Translate content="failoverDomain.page.cards.freeSpace" />
                                </LegendLabel>
                                <LegendValue>{formatSizeFromGiB(storage.free_space)}</LegendValue>
                            </LegendRow>
                        </StorageContent>
                    </Item>
                </CardItemContainer>
            </GridCol>

            {/* IP Blocks + Recent activities stacked */}
            <GridCol lg={3}>
                <StackedCardsContainer>
                    <CardItemContainer>
                        <Item
                            view="card"
                            title={
                                <CardHeader to={`${pathTo('ip_blocks')}?failover_domain=${failoverDomain.id}`}>
                                    <Translate content="failoverDomain.page.cards.ipBlocks" />
                                </CardHeader>
                            }
                        >
                            {failoverDomain.ip_blocks.length > 0 ? (
                                failoverDomain.ip_blocks.map(block => (
                                    <IpBlockRow key={block.id}>
                                        <IpBlockLink href={pathTo(`ip_blocks/${block.id}`)}>
                                            {block.type ?? block.name}
                                        </IpBlockLink>
                                        <IpBlockUsage>
                                            {block.reserved_ips_count} / {formatIpBlockTotal(block)}
                                        </IpBlockUsage>
                                    </IpBlockRow>
                                ))
                            ) : (
                                <EmptyCardText>
                                    <Translate content="failoverDomain.page.cards.noIpBlocks" />
                                </EmptyCardText>
                            )}
                        </Item>
                    </CardItemContainer>
                    <CardItemContainer>
                        <Item
                            view="card"
                            title={
                                <CardHeader to={pathTo(`failover_domains/${failoverDomain.id}/activities`)}>
                                    <Translate content="failoverDomain.page.cards.recentActivitiesTitle" />
                                </CardHeader>
                            }
                        >
                            <ActivityCount>
                                {failoverDomain.recent_activities_count}
                            </ActivityCount>
                        </Item>
                    </CardItemContainer>
                </StackedCardsContainer>
            </GridCol>

        </Grid>
    );
};

export default SummaryCards;
